// iOS-style interactive edge-swipe core, shared by page navigation (SwipeBack)
// and modal sheets (SheetShell): a touch starting at the left screen edge drags
// the target element to the right with the finger; releasing past a third of
// its width (or a fast flick) fires onCommit — otherwise it springs home.
// Transforms exist only WHILE dragging and are fully cleared afterwards, so
// position:fixed descendants are never trapped by a transformed ancestor.
import { useEffect, useRef, type RefObject } from "react";
import { hapticTap } from "./haptics";

// A fast bezel flick often REGISTERS its first touch 40-80px into the screen
// (iOS defers edge touches; the finger has already moved when touchstart
// lands), so the arming zone must be much wider than the visual "edge".
// Stealing scrolls is prevented by the engage step, not the arm zone.
const EDGE_PX = 64;            // touch must start this close to the left edge
const ENGAGE_DX = 8;           // horizontal travel that engages the gesture
const DOMINANCE = 1.2;         // dx must beat dy by this factor to engage
const COMMIT_RATIO = 0.3;      // fraction of width that commits on release
const COMMIT_VELOCITY = 0.3;   // px/ms flick that commits regardless of distance
// Apple's standard deceleration curve (UIKit sheets/navigation) — the single
// biggest "feels native" lever versus a stock ease-out.
const APPLE_EASE = "cubic-bezier(0.32, 0.72, 0, 1)";
const clampMs = (ms: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, ms));

export function useEdgeSwipe(
  containerRef: RefObject<HTMLElement | null>,
  opts: {
    /** Disable without unmounting (e.g. no back target on tab roots). */
    enabled?: boolean;
    onCommit: () => void;
    /** Element to translate while dragging; defaults to the container. */
    targetRef?: RefObject<HTMLElement | null>;
    /** Optional scrim that fades with drag progress (sheets pass theirs). */
    backdropRef?: RefObject<HTMLElement | null>;
  },
) {
  // Keep the latest options in refs so the listeners bind exactly once.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let startX = 0, startY = 0, lastX = 0, lastT = 0;
    let velo = 0; // recency-weighted px/ms, tracked across moves — a flick's
                  // touchend lands where the last move did, so measuring
                  // move→end would always read ~0 velocity.
    let armed = false, engaged = false, animating = false;

    const target = () => optsRef.current.targetRef?.current ?? el;
    const backdrop = () => optsRef.current.backdropRef?.current ?? null;
    const clear = () => {
      const tgt = target();
      tgt.style.transition = "";
      tgt.style.transform = "";
      tgt.style.opacity = "";
      tgt.style.boxShadow = "";
      const bd = backdrop();
      if (bd) { bd.style.transition = ""; bd.style.opacity = ""; }
    };

    const onStart = (e: TouchEvent) => {
      if (animating || optsRef.current.enabled === false || e.touches.length !== 1) return;
      const t = e.touches[0];
      if (t.clientX > EDGE_PX) return;
      armed = true;
      engaged = false;
      velo = 0;
      startX = lastX = t.clientX;
      startY = t.clientY;
      lastT = e.timeStamp;
    };

    const onMove = (e: TouchEvent) => {
      if (!armed) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (!engaged) {
        if (dx > ENGAGE_DX && Math.abs(dx) > Math.abs(dy) * DOMINANCE) {
          engaged = true;
          target().style.transition = "none";
        } else if (Math.abs(dy) > 16) {
          armed = false; // clearly a vertical scroll — release the gesture
          return;
        }
      }
      if (engaged) {
        e.preventDefault(); // we own this touch: no scrolling underneath
        const tgt = target();
        const x = Math.max(0, dx);
        tgt.style.transform = `translateX(${x}px)`;
        // Depth, not fade: native keeps the dragged view opaque and casts a
        // soft shadow onto whatever it slides over.
        tgt.style.boxShadow = "-16px 0 40px rgba(0, 0, 0, 0.35)";
        const bd = backdrop();
        if (bd) { bd.style.transition = "none"; bd.style.opacity = String(1 - Math.min(1, x / tgt.offsetWidth)); }
        const dt = e.timeStamp - lastT;
        if (dt > 0) velo = 0.8 * ((t.clientX - lastX) / dt) + 0.2 * velo;
        lastX = t.clientX;
        lastT = e.timeStamp;
      }
    };

    const finish = (e: TouchEvent) => {
      if (!armed) return;
      armed = false;
      if (!engaged) return;
      engaged = false;
      const endX = e.changedTouches[0]?.clientX ?? lastX;
      const dx = Math.max(0, endX - startX);
      const tgt = target();
      const width = tgt.offsetWidth;
      const commit = dx > width * COMMIT_RATIO || (velo > COMMIT_VELOCITY && dx > 40);

      animating = true;
      const bd = backdrop();
      if (commit) {
        // Finish at (roughly) the speed the finger was moving: remaining
        // distance over velocity, clamped so a slow release still glides and
        // a flick doesn't teleport.
        const dur = clampMs((width - dx) / Math.max(velo, 0.9), 150, 340);
        hapticTap();
        tgt.style.transition = `transform ${dur}ms ${APPLE_EASE}`;
        tgt.style.transform = `translateX(${width}px)`;
        if (bd) { bd.style.transition = `opacity ${dur}ms ${APPLE_EASE}`; bd.style.opacity = "0"; }
        let fired = false;
        const done = () => {
          if (fired) return;
          fired = true;
          tgt.removeEventListener("transitionend", done);
          optsRef.current.onCommit();
          clear();
          animating = false;
        };
        tgt.addEventListener("transitionend", done);
        // Safety net in case transitionend never fires (e.g. display change).
        setTimeout(done, dur + 80);
      } else {
        const dur = clampMs(dx / 0.9, 180, 300);
        tgt.style.transition = `transform ${dur}ms ${APPLE_EASE}`;
        tgt.style.transform = "translateX(0)";
        if (bd) { bd.style.transition = `opacity ${dur}ms ${APPLE_EASE}`; bd.style.opacity = "1"; }
        setTimeout(() => { clear(); animating = false; }, dur + 40);
      }
    };

    const onCancel = () => {
      armed = false;
      if (engaged) { engaged = false; clear(); }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", finish);
    el.addEventListener("touchcancel", onCancel);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", finish);
      el.removeEventListener("touchcancel", onCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
